/********************
  Created via mfCLI v2.0.0
  mfCLI Date: 2023-02-15 11:35:00
  Doc: https://marketfuel.getoutline.com/doc/mf-grid-cli-tool-mfcli-CQt1bFpg9u
  Grid Documentation: https://marketfuel.getoutline.com/doc/mf-grid-component-x54vV0bdDu
  Functional Documentation: https://marketfuel.getoutline.com/doc/mfgrid-functional-AfNInsXfEF
  OrgPermissionsGrid:
  created at Tue Jul 30 2024 21:29:44 GMT-0400 (Eastern Daylight Time) by dev_briann
********************/

/* GENERAL IMPORTS */
import isObjectEmpty from '@components/general/isObjectEmpty'
import {
  onActionButtonFirstItemClick,
  onActionButtonAnyItemClick
} from '@components/general/mfgrid/helpers/onActionButtons'
import useGridEffect from '@components/general/mfgrid/helpers/useGridEffect'
import useGridSelection from '@components/general/mfgrid/helpers/useGridSelection'
import {
  _actionButtonElements,
  _createButton,
  _editingOptions,
  _gridNotLoaded,
  _headerFilter,
  _searchPanel,
  _tableViewName,
  _urlButtons,
  _validateAndAssignActionButtonsMode
} from '@components/general/mfgrid/MfGridDefaults'
import MfGridFunctional from '@components/general/mfgrid/MfGridFunctional'
import { deleteInLineRow } from '@src/actions'
import { useState, useRef } from 'react'
import { useDispatch, useSelector, connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
/* GRID IMPORTS */

import config from './OrgPermissionsGridConfig'

const OrgPermissionsGrid = ({ onPermissionsChange }) => {
  /*
   ************** INIT **************
   */
  /* GENERAL */
  const mfGridComponentRef = useRef(null)
  const dispatch = useDispatch()
  let createButton = {}
  let editingOptions = {}
  let firstButtonConfig = {}
  let gridNotLoaded = ''
  let headerFilter = {}
  let searchPanel = {}
  let urlCrudButtons = []

  /* ADMIN */
  const currentAdmin = useSelector(state => state.admins.current_admin)
  const isOnlyForSuperAdmin = false
  const isSuperAdmin = currentAdmin.is_super_admin

  /* ROW BUTTONS TO EDIT, DELETE, ETC" */
  const enableInlineEditing = false
  const createButtonMode = enableInlineEditing ? 'inline' : 'row'
  const editingMode = 'row' // popup or row
  const showRowButtons = false // not needed if enableInlineEditing is true
  /* FILTERS */
  const [filter, setFilter] = useState({
    /*
    // Example of a filter:
    portal_id: 16,
    status: 'paid',
    // (any other key value pair. Ex: portal_id: portal.id)
    */
  })
  const [needsRefresh, setNeedsRefresh] = useState(false)
  const showFilterButtons = false
  const showFilterRow = false //// search by column includes the header filter
  const showHeaderFilter = false

  // /* ACTION BUTTONS */
  const actionButtons = []
  const actionButtonsConfig = {
    showActionButtons: false,
    showFirstButton: false,
    showFirstButtonOnly: false,
    showCreateButton: false,
    buttonsWidth: '180' // adjust the width of the action buttons
  }
  const {
    actionButtonDevMessage,
    actionButtonsMode,
    isValid,
    showActionButtons,
    showCreateButton,
    showFirstButton,
    showFirstButtonOnly
  } = actionButtonsConfig

  _validateAndAssignActionButtonsMode(actionButtonsConfig)

  /* ROW SELECTION */
  const showCheckBoxesMode = 'none' // always, onClick, none
  const filterButtonsInitialValue = 'All'
  const { selectedRowsData, selectedRowKeys, selectedItemKeys, setSelectedItemKeys, onSelectionChanged } =
    useGridSelection(filterButtonsInitialValue)

  const gridConfig = useGridEffect(isOnlyForSuperAdmin, isSuperAdmin, currentAdmin, filter, config)

  if (!isObjectEmpty(gridConfig)) {
    editingOptions = _editingOptions(gridConfig, enableInlineEditing, editingMode)
    gridNotLoaded = _gridNotLoaded()
    headerFilter = _headerFilter(showHeaderFilter)
    searchPanel = _searchPanel(showFilterRow)
    urlCrudButtons = showRowButtons && !enableInlineEditing ? _urlButtons(gridConfig) : {}
    /*
      -- To add an extra button
        urlCrudButtons.push({..., onClick: 'onClickExtraButton01',})
      -- To remove a button
        urlCrudButtons = urlCrudButtons.filter(button => button.name !== "Delete");
    */
  }

  // ************** END INIT **************

  const setFirstButton = () => {
    if ((showFirstButton || showFirstButtonOnly) && showCreateButton) {
      createButton = showCreateButton
        ? _createButton(gridConfig, enableInlineEditing, showCreateButton, createButtonMode)
        : {}
      firstButtonConfig = {
        ...createButton
      }
    } else if (actionButtonsConfig.actionButtonsMode === 'oneActionButtonOnly') {
      firstButtonConfig = {
        id: 1,
        key: `firstButton${gridConfig.storageKey}`,
        onButtonClick: 'onActionButtonFirstItemClick',
        permissions: `${gridConfig.permissions}.CREATE`,
        stylingMode: 'outlined',
        text: 'Only One Action Button',
        type: 'default'
      }
    }
    return firstButtonConfig
  }

  const setActionButtons = () => {
    const thisFirstButton = setFirstButton()
    if (showFirstButton) {
      actionButtons.unshift(thisFirstButton)
    }
    let actionButtonElements = []
    if (showActionButtons && showFirstButtonOnly === false) {
      actionButtonElements = _actionButtonElements(gridConfig)
      /* ========== NOTE ==========
       * _actionButtonElements(gridConfig) will show you AN EXAMPLE of how to create action buttons
       * Please redefine it according to your needs
       * DO NOT CHANGE THE FILE MfGridDefaults IN ANY WAY
       * ==========       ==========*/
      actionButtons.push(...actionButtonElements)
      return actionButtons
    }
  }

  /* FILTER BUTTONS */
  const filterButtons = () => {
    const buttons = [
      { icon: 'fa-regular fa-filter-slash', text: 'All', type: 'normal', visible: true },
      { icon: 'fa-regular fa-display-arrow-down', text: 'One', type: 'success', visible: true },
      { icon: 'fa-regular fa-boxes-stacked', text: 'None', type: 'danger', visible: true }
    ]
    return {
      props: { id: 'order_filters', visible: true, stylingMode: 'outlined', selectedItemKeys },
      buttons
    }
  }

  const onClickButtonFilter = e => {
    let selectedFilter = e.itemData.text
    const selectedItemKeys = e.itemData.text
    /* This block is an example of how to handle different filters
   when the text of the button is not the what is to be filtered. */
    switch (e.itemData.text) {
      case filterButtonsInitialValue:
        break
      case 'One':
        selectedFilter = 1
        break
      case 'None':
        selectedFilter = 0
        break
      default:
      // Code to handle other cases, if needed
    }
    setSelectedItemKeys([selectedItemKeys])
    if (e.itemData.text === filterButtonsInitialValue) {
      setFilter({})
    } else {
      setFilter({ ...filter, id: selectedFilter })
    }
  }

  const onClearFilters = () => {
    setFilter({})
    if (showFilterButtons && 'props' in filterButtons) {
      filterButtons.props.selectedItemKeys = [filterButtonsInitialValue]
    }
  }

  const onClickExtraButton01 = dataKey => {
    /*
      In case you have an extra button on the grid.
      If you don't need it, delete and the related function on
      the MfGridFunctional component
    */
    alert(`Extrabutton01 - ${dataKey}`)
  }

  const onSaving = dataChangeEvent => {}

  const onRowRemoving = row => {
    const gridRefCurrent = mfGridComponentRef?.current || false
    const tableViewName = _tableViewName(gridRefCurrent)
    row.cancel = true
    const rowId = row.key
    if (rowId && tableViewName) {
      dispatch(deleteInLineRow(rowId, tableViewName))
    }
    setNeedsRefresh(true)
  }

  const onCellClick = e => {
    const crudType = e.column.dataField
    const newValue = !e.value
    e.component.cellValue(e.rowIndex, crudType, newValue)
    const orgPermissionGroupId = e.data.id
    onPermissionsChange(crudType, orgPermissionGroupId, newValue)
  }

  const handlePermissionsUpdate = newPermissions => {}

  if (_validateAndAssignActionButtonsMode(actionButtonsConfig).isValid === false) {
    return <div>{_validateAndAssignActionButtonsMode(actionButtonsConfig).actionButtonsMode}</div>
  } else {
    return (
      <>
        <label>Permissions</label>
        {gridConfig && gridConfig.gridUrl ? (
          <MfGridFunctional
            actionButtonsConfig={{
              ...actionButtonsConfig,
              actionButtons: setActionButtons(),
              createButton,
              firstButtonConfig
            }}
            columnsConfig={{
              columns: gridConfig.columns
            }}
            dataConfig={{
              idSortOrder: gridConfig.idSortOrder,
              sqlFields: gridConfig.sqlFields,
              sqlAppendix: {
                enabled: true,
                appendix: `` //EXAMPLE: `portal_id = ${props.portal_id}`
              }
            }}
            editingConfig={{
              editing: enableInlineEditing,
              editingOptions,
              urlCrudButtons: !isObjectEmpty(urlCrudButtons) ? urlCrudButtons : null,
              hideDeleteButton: true
            }}
            eventsConfig={{
              onActionButtonFirstItemClick: data =>
                onActionButtonFirstItemClick(
                  data,
                  actionButtonsConfig,
                  createButtonMode,
                  mfGridComponentRef,
                  props.history
                ),
              onActionButtonAnyItemClick: data => onActionButtonAnyItemClick(data, props.history),
              onClearFilters,
              onClickButtonFilter,
              onClickExtraButton01,
              onRowRemoving,
              onSaving,
              onSelectionChanged: data => onSelectionChanged(data, showCheckBoxesMode)
            }}
            filterConfig={{
              filter: gridConfig.filter,
              filterButtons: showFilterButtons ? filterButtons() : null,
              gridToRefresh: 'MfGridFunctional',
              headerFilter,
              needsRefresh,
              searchPanel,
              showFilterRow,
              showFilterButtons
            }}
            gridConfig={{
              dataKey: gridConfig.dataKey,
              gridRef: mfGridComponentRef,
              gridUrl: gridConfig.gridUrl,
              storageKey: `${gridConfig.storageKey}-mfgrid${gridConfig.titleNoSpace}`,
              titleNoSpace: gridConfig.titleNoSpace
            }}
            masterDetailConfig={{
              autoExpandAll: false,
              enabled: false,
              component: null
            }}
            // rowConfig={{
            //     // check MfGridFunctional file for props                     }
            // }}
            selectionConfig={{
              rowSelectionMode: 'multiple',
              selectAllMode: 'page',
              selectedRowKeys,
              showCheckBoxesMode
            }}
            // onRowClick={handleRowClick}
            onCellClick={onCellClick}
          ></MfGridFunctional>
        ) : (
          <div>{gridNotLoaded}</div>
        )}
      </>
    )
  }
}
export default withRouter(connect(null, { deleteInLineRow })(OrgPermissionsGrid))
